import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Upload, Link2, Trash2, Printer, FileText, Image as ImageIcon, FileType, Waves, Trophy } from "lucide-react";

const KINDS = ["link", "image", "pdf", "doc", "note"];
const SUBJECTS = ["general", "math", "reading-writing", "oceanography", "history", "spanish", "creative-tech-art"];

export default function Portfolio() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const filterStudent = params.get("student_id") || "";
  const [items, setItems] = useState([]);
  const [cover, setCover] = useState(null);
  const [subject, setSubject] = useState("all");
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", subject: "general", kind: "link", url: "", tags: [] });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const path = filterStudent ? `/portfolio?student_id=${filterStudent}` : "/portfolio";
    const { data } = await api.get(path);
    setItems(data);
    // Fetch cover data when filtered to a single student (or when student viewing own)
    const cid = filterStudent || (user?.role === "student" ? user.id : null);
    if (cid) {
      try { const c = await api.get(`/portfolio/cover/${cid}`); setCover(c.data); }
      catch { setCover(null); }
    } else {
      setCover(null);
    }
  };
  useEffect(() => { load(); }, [filterStudent, user?.id]);

  const readFileAsDataUrl = (f) => new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(f);
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title) return toast.error("Title required");
    setBusy(true);
    try {
      const payload = { ...form };
      if (["image", "pdf"].includes(form.kind) && file) {
        if (file.size > 4 * 1024 * 1024) return toast.error("Please keep uploads under 4 MB");
        payload.file_data = await readFileAsDataUrl(file);
        payload.file_name = file.name;
      }
      await api.post("/portfolio", payload);
      toast.success("Added to portfolio · +5 points");
      setForm({ title: "", description: "", subject: "general", kind: "link", url: "", tags: [] });
      setFile(null); setShowAdd(false); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!confirm("Remove from portfolio?")) return;
    try { await api.delete(`/portfolio/${id}`); load(); toast.success("Removed"); }
    catch { toast.error("Could not remove"); }
  };

  const filtered = subject === "all" ? items : items.filter((x) => x.subject === subject);

  return (
    <div className="space-y-6" data-testid="portfolio-page">
      <div className="print:hidden flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <div className="chip mb-2">Portfolio</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900">Finished work, all in one place</h1>
          <p className="text-slate-600 mt-1 max-w-2xl">Google Docs, PDFs, photos, project screenshots. Filter, print, and show it to the school board with one tap.</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="portfolio-print" onClick={()=>window.print()} className="px-4 py-2.5 rounded-full border border-slate-200 bg-white text-sm font-semibold inline-flex items-center gap-1"><Printer size={14} /> Print</button>
          {user?.role === "student" && (
            <button data-testid="portfolio-add-toggle" onClick={()=>setShowAdd((s)=>!s)} className="px-4 py-2.5 rounded-full bg-teal-700 hover:bg-teal-800 text-white text-sm font-semibold inline-flex items-center gap-1"><Plus size={14} /> Add item</button>
          )}
        </div>
      </div>

      {showAdd && user?.role === "student" && (
        <form onSubmit={submit} className="print:hidden p-6 rounded-3xl bg-white border border-slate-200 grid sm:grid-cols-2 gap-3" data-testid="portfolio-add-form">
          <input data-testid="pf-title" required placeholder="Title (e.g., ‘Coral Reef Infographic’)" value={form.title} onChange={(e)=>setForm({...form, title: e.target.value})} className="sm:col-span-2 px-4 py-3 rounded-xl border border-slate-200" />
          <select data-testid="pf-subject" value={form.subject} onChange={(e)=>setForm({...form, subject: e.target.value})} className="px-4 py-3 rounded-xl border border-slate-200 bg-white">
            {SUBJECTS.map((s) => <option key={s}>{s}</option>)}
          </select>
          <select data-testid="pf-kind" value={form.kind} onChange={(e)=>setForm({...form, kind: e.target.value})} className="px-4 py-3 rounded-xl border border-slate-200 bg-white">
            {KINDS.map((k) => <option key={k}>{k}</option>)}
          </select>
          {["link", "doc"].includes(form.kind) && (
            <input data-testid="pf-url" required placeholder="URL (Google Docs, Canva, Scratch…)" value={form.url} onChange={(e)=>setForm({...form, url: e.target.value})} className="sm:col-span-2 px-4 py-3 rounded-xl border border-slate-200" />
          )}
          {["image", "pdf"].includes(form.kind) && (
            <label className="sm:col-span-2 px-4 py-3 rounded-xl border border-dashed border-slate-300 bg-slate-50 flex items-center gap-2 cursor-pointer">
              <Upload size={16} className="text-slate-500" />
              <span className="text-sm text-slate-600">{file ? file.name : `Choose ${form.kind === "image" ? "image" : "PDF"} (< 4 MB)`}</span>
              <input data-testid="pf-file" type="file" accept={form.kind === "image" ? "image/*" : "application/pdf"} onChange={(e)=>setFile(e.target.files?.[0] || null)} className="hidden" />
            </label>
          )}
          <textarea data-testid="pf-description" placeholder="Description (what it is, what you're proud of)" value={form.description} onChange={(e)=>setForm({...form, description: e.target.value})} className="sm:col-span-2 px-4 py-3 rounded-xl border border-slate-200" rows={2} />
          <button data-testid="pf-submit" disabled={busy} className="sm:col-span-2 px-6 py-3 rounded-full bg-teal-700 hover:bg-teal-800 text-white font-semibold disabled:opacity-60">{busy ? "Saving…" : "Add to portfolio"}</button>
        </form>
      )}

      <div className="print:hidden flex flex-wrap gap-1 rounded-full bg-slate-100 p-1 w-fit">
        {["all", ...SUBJECTS].map((s) => (
          <button key={s} data-testid={`pf-filter-${s}`} onClick={()=>setSubject(s)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold ${subject === s ? "bg-white text-teal-800 shadow-sm" : "text-slate-500"}`}>
            {s}
          </button>
        ))}
      </div>

      {/* Printable cover page (only when viewing a single student's portfolio) */}
      {cover && (
        <section className="hidden print:block break-after-page" data-testid="portfolio-cover">
          <div className="flex items-center gap-3 mb-8">
            <span className="grid place-items-center w-12 h-12 rounded-2xl bg-teal-700 text-white"><Waves size={22} /></span>
            <div>
              <div className="font-display text-2xl font-bold text-slate-900">Oceanic Learn</div>
              <div className="text-sm text-slate-500">Homeschool portfolio · school-board packet</div>
            </div>
          </div>
          <h2 className="font-display text-4xl font-bold text-slate-900">{cover.student.name}</h2>
          <div className="text-slate-600 mt-1">{cover.student.email}</div>
          <div className="text-sm text-slate-500 mt-1">Generated {new Date(cover.generated_at).toLocaleDateString()}</div>
          <div className="grid grid-cols-3 gap-4 mt-8">
            <div className="p-4 rounded-2xl bg-slate-50 border border-slate-100">
              <div className="text-xs text-slate-500 uppercase tracking-widest">Points</div>
              <div className="font-display text-2xl font-bold">{cover.student.points ?? 0}</div>
            </div>
            <div className="p-4 rounded-2xl bg-slate-50 border border-slate-100">
              <div className="text-xs text-slate-500 uppercase tracking-widest">Portfolio items</div>
              <div className="font-display text-2xl font-bold">{cover.portfolio_total}</div>
            </div>
            <div className="p-4 rounded-2xl bg-slate-50 border border-slate-100">
              <div className="text-xs text-slate-500 uppercase tracking-widest flex items-center gap-1"><Trophy size={12} /> Achievements</div>
              <div className="font-display text-2xl font-bold">{cover.achievements_total}</div>
            </div>
          </div>
          <h3 className="font-display text-lg font-semibold text-slate-900 mt-8 mb-3">Subject scorecard</h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500 border-b border-slate-200">
                <th className="py-2">Subject</th><th>Lessons</th><th>Portfolio items</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(cover.by_subject).map(([slug, s]) => (
                <tr key={slug} className="border-b border-slate-100">
                  <td className="py-2 text-slate-900">{s.title}</td>
                  <td>{s.lessons_done} / {s.lessons_total}</td>
                  <td>{s.portfolio_items}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-10 pt-4 border-t border-slate-200 text-xs text-slate-500">
            Oceanic Learn portfolio · {new Date(cover.generated_at).toLocaleString()}
          </div>
        </section>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="portfolio-grid">
        {filtered.map((it) => (
          <div key={it.id} data-testid={`pf-item-${it.id}`} className="p-4 rounded-2xl bg-white border border-slate-200 hover:border-teal-300 transition-colors">
            <div className="flex items-start justify-between gap-2">
              <div className="chip capitalize">{it.subject}</div>
              <span className="text-xs text-slate-500 flex items-center gap-1">
                {it.kind === "image" ? <ImageIcon size={12} /> : it.kind === "pdf" ? <FileType size={12} /> : it.kind === "doc" ? <FileText size={12} /> : <Link2 size={12} />}
                {it.kind}
              </span>
            </div>
            {it.kind === "image" && it.file_data && (
              <img src={it.file_data} alt={it.title} className="mt-3 w-full aspect-video object-cover rounded-xl" />
            )}
            {it.kind === "pdf" && it.file_data && (
              <a href={it.file_data} download={it.file_name || `${it.title}.pdf`} className="mt-3 block p-6 bg-red-50 rounded-xl text-center text-red-700 font-semibold"><FileType className="mx-auto" /> Open PDF</a>
            )}
            <div className="mt-3 font-display text-lg font-semibold text-slate-900">{it.title}</div>
            {it.description && <p className="text-sm text-slate-600 mt-1">{it.description}</p>}
            {it.url && (
              <a href={it.url} target="_blank" rel="noreferrer" className="mt-2 text-sm text-teal-700 font-semibold break-all inline-flex items-center gap-1">
                <Link2 size={12} /> {it.url.length > 40 ? it.url.slice(0, 40) + "…" : it.url}
              </a>
            )}
            <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
              <span>{new Date(it.created_at).toLocaleDateString()}</span>
              {(user?.role === "student" || user?.role === "parent") && (
                <button data-testid={`pf-remove-${it.id}`} onClick={()=>remove(it.id)} className="text-red-500 hover:text-red-700 print:hidden"><Trash2 size={14} /></button>
              )}
            </div>
          </div>
        ))}
        {filtered.length === 0 && <div className="text-slate-500 text-sm col-span-full">Nothing in this filter yet.</div>}
      </div>

      <style>{`@media print { @page { size: A4; margin: 12mm; } nav, aside, .print\\:hidden { display: none !important; } main { padding: 0 !important; } .grid { display: block !important; } [data-testid^="pf-item-"] { break-inside: avoid; margin-bottom: 12mm; } }`}</style>
    </div>
  );
}
